import { useMemo, useState } from 'react'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { useHasPermissions } from '@/lib/app/check-permissions.hook'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'

import { AssessmentRoomProvider } from '@/components/shared/comment/assessment-room-provider'
import { COMMENT_EVENT_SOURCES } from '@/components/shared/comment/consts'
import { CommentAudienceProvider } from '@/components/shared/comment/context'

import { AssessmentCommentsBody } from './assessment-comments-body'
import { AssessmentCommentsHeader } from './assessment-comments-header'
import { AssessmentCommentsSkeleton } from './assessment-comments-skeleton'
import type { CommentFilter } from './types'

type AssessmentCommentsPanelProps = {
  assessmentId: string
}

export const AssessmentCommentsPanel = ({ assessmentId }: AssessmentCommentsPanelProps) => {
  const readOnly = !useHasPermissions(['base.manage'])
  const analytics = useAssessmentAnalytics()
  const trackFilterChanged = useTrackCallback('assessment.comment.filter.changed')
  const [search, setSearch] = useState<string | null>(null)
  const [filter, setFilter] = useState<CommentFilter>('all')

  const trackingProps: EventProperties = useMemo(
    () => ({
      assessment_id: assessmentId,
      company_id: analytics?.companyId,
      company: analytics?.companyName,
      source: COMMENT_EVENT_SOURCES.ASSESSMENT_PANEL,
    }),
    [assessmentId, analytics?.companyId, analytics?.companyName],
  )

  const changeFilter = (next: CommentFilter) => {
    if (next === filter) return
    setFilter(next)
    trackFilterChanged({ ...trackingProps, filter: next })
  }

  return (
    <CommentAudienceProvider value='internal'>
      <div className='flex min-h-0 flex-1 flex-col gap-3 pb-6 pt-3'>
        <AssessmentCommentsHeader
          search={search}
          onSearchChange={setSearch}
          filter={filter}
          onFilterChange={changeFilter}
        />

        <div className='flex min-h-0 flex-1 flex-col px-6'>
          <AssessmentRoomProvider
            assessmentId={assessmentId}
            fallback={
              <div className='flex min-h-0 flex-1 flex-col'>
                <AssessmentCommentsSkeleton />
              </div>
            }
          >
            <AssessmentCommentsBody
              assessmentId={assessmentId}
              search={search}
              filter={filter}
              readOnly={readOnly}
              trackingProps={trackingProps}
            />
          </AssessmentRoomProvider>
        </div>
      </div>
    </CommentAudienceProvider>
  )
}
